import React, { useMemo, useState } from 'react'
import {
  IAnomaliesV2Network,
  IRejectionReviewAdvertiserMonth,
} from '../../shared/types'
import { formatRejectionReviewMonth } from '../RejectionReview'

// Monthly-grain "Networks active" lane that mirrors the daily NetworkLane on
// the triage detail page. One row per network, one column per month.
//
// Presence is derived from the top-level `networks[]` array on the response:
// each network has month-grain `firstSeen` / `lastSeen`, so a network is
// considered active in every month inclusive between those two values. If
// per-month `networkUuidsContext` strings are also present they take
// precedence for finer-grain accuracy.

const PALETTE = [
  '#4F9EEC',
  '#5DC97E',
  '#A675F5',
  '#EBB617',
  '#E55353',
  '#3FB8B5',
  '#F08AB5',
  '#7A8BFF',
]

const splitNetworkUuids = (context: string | null | undefined): string[] => {
  if (!context) return []
  return context.split('|').filter(Boolean)
}

export const RejectionNetworkLane = (props: {
  months: IRejectionReviewAdvertiserMonth[]
  networks?: IAnomaliesV2Network[]
  selectedMonth?: string | null
  onSelectMonth?: (month: string) => void
}) => {
  const { months, networks, selectedMonth, onSelectMonth } = props

  // Sorted ASC so the lane reads left→right oldest→newest, matching the
  // chart axes above it.
  const orderedMonths = useMemo(
    () =>
      [...months].sort((a, b) =>
        a.conversionMonth.localeCompare(b.conversionMonth)
      ),
    [months]
  )

  // Detect whether month rows carry per-month UUID context (some endpoints
  // do, some don't). If yes — use it directly. If no — derive presence from
  // each network's firstSeen / lastSeen interval.
  const hasMonthLevelUuids = useMemo(
    () => orderedMonths.some((m) => !!m.networkUuidsContext),
    [orderedMonths]
  )

  const presence = useMemo(() => {
    const map = new Map<string, Set<string>>()
    if (hasMonthLevelUuids) {
      for (const m of orderedMonths) {
        for (const uuid of splitNetworkUuids(m.networkUuidsContext)) {
          const set = map.get(uuid) ?? new Set<string>()
          set.add(m.conversionMonth)
          map.set(uuid, set)
        }
      }
      return map
    }
    // Fallback: interval-fill from firstSeen / lastSeen on the networks array.
    for (const n of networks || []) {
      const set = new Set<string>()
      for (const m of orderedMonths) {
        if (
          m.conversionMonth >= n.firstSeen &&
          m.conversionMonth <= n.lastSeen
        ) {
          set.add(m.conversionMonth)
        }
      }
      map.set(n.uuid, set)
    }
    return map
  }, [orderedMonths, networks, hasMonthLevelUuids])

  // Unique network UUIDs ordered by first-seen month so colours stay stable
  // across renders.
  const orderedUuids = useMemo(() => {
    if (hasMonthLevelUuids) {
      const firstSeen = new Map<string, string>()
      for (const m of orderedMonths) {
        for (const uuid of splitNetworkUuids(m.networkUuidsContext)) {
          if (!firstSeen.has(uuid)) firstSeen.set(uuid, m.conversionMonth)
        }
      }
      return Array.from(firstSeen.keys()).sort((a, b) =>
        (firstSeen.get(a) || '').localeCompare(firstSeen.get(b) || '')
      )
    }
    return [...(networks || [])]
      .sort((a, b) => a.firstSeen.localeCompare(b.firstSeen))
      .map((n) => n.uuid)
  }, [orderedMonths, networks, hasMonthLevelUuids])

  const nameFor = useMemo(() => {
    const map = new Map<string, string>()
    for (const n of networks || []) map.set(n.uuid, n.name)
    return map
  }, [networks])

  const colourFor = useMemo(() => {
    const m = new Map<string, string>()
    orderedUuids.forEach((uuid, i) => m.set(uuid, PALETTE[i % PALETTE.length]))
    return m
  }, [orderedUuids])

  const [hover, setHover] = useState<
    { month: string; label: string; active: boolean } | null
  >(null)

  if (orderedUuids.length === 0 || orderedMonths.length === 0) return null

  return (
    <div className="card anomalies-network-lane">
      <div className="card-body">
        <div
          className="row row-space-between row-center m-b-2"
          style={{ gap: 12, flexWrap: 'wrap' }}
        >
          <div className="text-dark text-bolder">Networks active</div>
          <div className="text-light text-small">
            {hover ? (
              <>
                <b>{hover.label}</b> · {formatRejectionReviewMonth(hover.month)}{' '}
                · {hover.active ? 'active' : 'inactive'}
              </>
            ) : (
              <>One row per network · cell filled when active that month</>
            )}
          </div>
        </div>

        <div className="anomalies-network-lane-grid">
          {orderedUuids.map((uuid) => {
            const dates = presence.get(uuid) ?? new Set<string>()
            const colour = colourFor.get(uuid) || '#828B9E'
            const label = nameFor.get(uuid) || uuid
            return (
              <div key={uuid} className="anomalies-network-lane-row">
                <div className="anomalies-network-lane-label">
                  <span
                    className="anomalies-network-lane-swatch"
                    style={{ background: colour }}
                  />
                  <span className="text-dark text-small" title={uuid}>
                    {label}
                  </span>
                  <span className="text-light text-small">
                    {dates.size} mo
                  </span>
                </div>
                <div
                  className="anomalies-network-lane-track"
                  onMouseLeave={() => setHover(null)}
                >
                  {orderedMonths.map((m) => {
                    const active = dates.has(m.conversionMonth)
                    const isSelected = selectedMonth === m.conversionMonth
                    return (
                      <div
                        key={m.conversionMonth}
                        className="anomalies-network-lane-cell"
                        title={`${label} · ${formatRejectionReviewMonth(m.conversionMonth)}${
                          active ? ' · active' : ' · inactive'
                        }`}
                        onMouseEnter={() =>
                          setHover({
                            month: m.conversionMonth,
                            label,
                            active,
                          })
                        }
                        onClick={() => onSelectMonth?.(m.conversionMonth)}
                        style={{
                          cursor: onSelectMonth ? 'pointer' : 'default',
                          background: active ? colour : 'transparent',
                          outline: isSelected
                            ? '1px solid rgba(130, 139, 158, 0.7)'
                            : undefined,
                        }}
                      />
                    )
                  })}
                </div>
              </div>
            )
          })}

          {/* Bottom axis row aligns with the same grid columns as the tracks. */}
          <div className="anomalies-network-lane-row anomalies-network-lane-axis-row">
            <div />
            <div className="anomalies-network-lane-axis">
              <span className="text-light text-small">
                {orderedMonths.length > 0
                  ? formatRejectionReviewMonth(
                      orderedMonths[0].conversionMonth
                    )
                  : ''}
              </span>
              <span className="text-light text-small">
                {orderedMonths.length > 0
                  ? formatRejectionReviewMonth(
                      orderedMonths[orderedMonths.length - 1].conversionMonth
                    )
                  : ''}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
